<script>
import { mapState } from 'vuex';
import isFunction from 'lodash/isFunction';
import flatPickr from 'vue-flatpickr-component';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import ScheduledServiceTimeModel from '@models/ScheduledServiceTime';
import JobModel from '@models/Job';
import CarTypeModel from '@models/CarType';
import DriverModel from '@models/Driver';
import ParamModel from '@models/Param';
import ServiceProviderModel from '@models/ServiceProvider';
import ReportModel from '@models/Report';
import LocationModel from '@models/Location';
import CarModel from '@models/Car';
import BlockingContainer from '@components/BlockingContainer';
import editFormChange from '@mixins/editFormChange';
import datePickerUtility from '@mixins/datePickerUtility';
import CarForm from '@views/cars/form';
import EntityMultiselect from '@components/EntityMultiselect';
import utility from '@mixins/utility';
import LocationForm from '@views/locations/form';

export default {
  components: {
    BlockingContainer,
    flatPickr,
    FontAwesomeIcon,
    CarForm,
    EntityMultiselect,
    LocationForm,
  },
  mixins: [editFormChange, datePickerUtility, utility],
  props: {
    // eslint-disable-next-line
    loadedId: {
      default: null,
    },
    // eslint-disable-next-line
    job: {
      required: true,
      default: null,
    },
    carTypesProp: {
      type: Array,
      required: false,
      default: () => [],
    },
  },
  data() {
    return {
      driverModel: DriverModel,
      reportModel: ReportModel,
      serviceProviderModel: ServiceProviderModel,
      locationModel: LocationModel,
      areas: [],
      showCreateLocationModal: false,
      createLocationType: null,
      useExistingCar: false,
      executorSelectedOption: 0,
      executorOptions: [
        { value: 0, text: this.$t('fields.driver') },
        { value: 1, text: this.$t('fields.service_provider') },
      ],
      reportMultiselectSelect: [],
      reportMultiselectInclude: ['vip', 'company', 'user'],
      reportMultiselectWhere: [
        {
          name: 'search',
        },
        {
          name: 'lock_status',
          value: true
        },
      ],
      reportMultiselectURLParams: {
        all_drafts: true,
      },
      localDriverId: null,
      localServiceProviderId: null,
      scheduledServiceTime: {
        car_type_id: null,
        location_from_id: null,
        location_from: null,
        location_to: null,
        location_to_id: null,
        start_timestamp: null,
        end_timestamp: null,
        destination: null,
        car_id: null,
        car: {
          license_plate: '',
          car_owner: {
            name: '',
          },
          car_owner_id: '',
          car_type: {
            name: '',
          },
          car_type_id: '',
          zub: [],
        },
        driver_id: null,
        driver: null,
        service_provider: null,
        service_provider_id: null,
        report_id: null,
        report: null,
        remark: null,
      },
      defaultScheduledServiceTime: {
        car_type_id: null,
        location_from_id: null,
        location_from: null,
        location_to: null,
        location_to_id: null,
        start_timestamp: null,
        end_timestamp: null,
        destination: null,
        car_id: null,
        car: {
          license_plate: '',
          car_owner: {
            name: '',
          },
          car_owner_id: '',
          car_type: {
            name: '',
          },
          car_type_id: '',
          zub: [],
        },
        driver_id: null,
        driver: null,
        service_provider: null,
        service_provider_id: null,
        report_id: null,
        report: null,
        remark: null,
      },
      carTypes: [],
      selectedCar: false,
      previousStartHour: null,
      previousEndHour: null,
    };
  },
  computed: {
    ...mapState({
      dateConfig: (state) => state.common.dateConfigTime,
    }),
  },
  mounted() {

    // fetch areas
    (new ParamModel).params({
          select_many: ['areas'].join(','),
        }).first()
    .then((response) => {
      this.areas = response.areas;
    });

    // add custom validation rules
    this.customValidationRules();
    // load car types
    this.$wait.start('car types loading');

    if (this.carTypesProp.length === 0) {
      CarTypeModel.select(['id', 'name'])
        .orderBy(`name`)
        .page(1)
        .limit(100)
        .get()
        .then((response) => {
          this.carTypes = response.data;
          this.$wait.end('car types loading');
        })
        .catch(() => {
          this.$wait.end('car types loading');
        });
    } else {
      this.carTypes = this.carTypesProp;
    }

    if (this.loadedId) {
      this.$wait.start('scheduled-service-time loading');
      new JobModel({ id: this.job.id })
        .scheduledServiceTimes()
        .find(this.loadedId)
        .then((response) => {
          this.scheduledServiceTime = Object.assign({}, this.scheduledServiceTime, response.scheduled_service_time);
          this.localDriverId = this.scheduledServiceTime.driver_id;
          this.localServiceProviderId = this.scheduledServiceTime.service_provider_id;

          // set car for form
          if (this.scheduledServiceTime.car_id && this.scheduledServiceTime.car) {
            this.scheduledServiceTime.car = this.scheduledServiceTime.car;
            this.useExistingCar = true;
          }

          if (this.scheduledServiceTime.service_provider_id != null) {
            this.executorSelectedOption = 1;
          }

          this.$wait.end('scheduled-service-time loading');
          this.$nextTick(() => {
            this.initChangeWatcher(['serviceTime'], this.validate);
          });
        });
    }
  },
  methods: {
    onExecutorChange(checked) {
      if (this.loadedId !== null) {
        this.resetSelection(checked);
      }
    },
    resetSelection(checked) {
      if (checked === 0) {
        this.scheduledServiceTime.service_provider_id = null;
        this.scheduledServiceTime.driver_id = this.localDriverId;
      } else {
        this.scheduledServiceTime.driver_id = null;
        this.scheduledServiceTime.service_provider_id = this.localServiceProviderId;
      }
    },
    assignServiceTimeDateValueFromTo(currentModel, nextModel) {
      if (currentModel != null) {
        this.scheduledServiceTime[nextModel] = this.scheduledServiceTime[currentModel];
      }
    },
    handleTimeChange(selectedDates, dateStr, instance, fieldName, previousHourField) {
      if (selectedDates.length === 0) return;
      
      const currentDate = selectedDates[0];
      const currentHour = currentDate.getHours();
      
      if (this[previousHourField] !== null) {
        // Check if we went from 23 to 00 (increment - move to next day)
        if (this[previousHourField] === 23 && currentHour === 0) {
          currentDate.setDate(currentDate.getDate() + 1);
          instance.setDate(currentDate, false);
          this.scheduledServiceTime[fieldName] = instance.formatDate(currentDate, 'Y-m-d H:i:S');
        }
        // Check if we went from 00 to 23 (decrement - move to previous day)
        else if (this[previousHourField] === 0 && currentHour === 23) {
          currentDate.setDate(currentDate.getDate() - 1);
          instance.setDate(currentDate, false);
          this.scheduledServiceTime[fieldName] = instance.formatDate(currentDate, 'Y-m-d H:i:S');
        }
      }
      
      this[previousHourField] = currentHour;
    },
    customValidationRules() {
      // add new validation rule
      this.$validator.extend('isBigger', (value, [otherValue]) => {
        return value >= otherValue;
      }, {
        hasTarget: true,
      });

      // set custom message
      this.$validator.localize({
        en: {
          messages: {
            isBigger: this.$t('errors.service_end_after_service_start'),
          }
        }
      })
    },
    resetValidation() {
      this.$validator.reset();
    },
    clearReport() {
      this.scheduledServiceTime.report_id = null;
      this.scheduledServiceTime.report = {};
    },
    validate(callback) {
      this.$validator.validateAll('scheduled-service-time-form').then((result) => {
        if (result) {
          this.prepareData(callback);
        }
      });
    },
    preSubmit(callback) {
      if (this.useExistingCar) {
        this.$wait.start('scheduled-service-time loading');
        // TODO maybe use $ref and submit car form instead
        new CarModel(
          Object.assign({}, this.scheduledServiceTime.car, {
            zub:
              this.scheduledServiceTime.car.zub && Array.isArray(this.scheduledServiceTime.car.zub)
                ? this.scheduledServiceTime.car.zub[0]
                : this.scheduledServiceTime.car.zub,
          })
        )
          .save()
          .then((response) => {
            this.scheduledServiceTime.car = response.car;
            this.scheduledServiceTime.car_id = response.car.id;
            this.submit(callback);
          })
          .catch(() => this.$wait.end('scheduled-service-time loading'));
      } else {
        this.submit(callback);
      }
    },
    prepareData(callback) {
      // set car properly
      if (this.useExistingCar) {
        this.scheduledServiceTime.car_type_id = null;
        this.scheduledServiceTime.car_id = this.scheduledServiceTime.car_id;
      } else {
        this.scheduledServiceTime.car = null;
        this.scheduledServiceTime.car_id = null;
      }
      this.preSubmit(callback);
    },
    submit(callback) {
      const job = new JobModel({ id: this.job.id });
      // fix for intersecting same fields
      this.scheduledServiceTime.job = job;
      new ScheduledServiceTimeModel(this.scheduledServiceTime)
        .for(job)
        .save()
        .then((response) => {
          this.scheduledServiceTime = Object.assign({}, this.defaultScheduledServiceTime);
          this.$validator.reset();
          this.$emit('submitted', response.scheduled_service_time);
          if (callback && isFunction(callback)) callback();
          this.$wait.end('scheduled-service-time loading');
        })
        .catch(() => this.$wait.end('scheduled-service-time loading'));
    },
    async deleteScheduledServiceTime() {
      if (confirm(this.$t('alerts.delete_entity', { entity: this.$t('entities.scheduled_service_time') }))) {
        this.$wait.start(`scheduled_service_time${this.scheduledServiceTime.id}`);
        const job = new JobModel({ id: this.job.id });
        const scheduledServiceTime = new ScheduledServiceTimeModel({ id: this.scheduledServiceTime.id });
        await scheduledServiceTime.for(job).delete();
        this.$emit('deleted');
        this.$wait.end(`scheduled_service_time${this.scheduledServiceTime.id}`);
      }
    },
    showLocationModal(type) {
      this.createLocationType = type;
      this.showCreateLocationModal = true;
    },
    closeLocationModal() {
      this.createLocationType = null;
      this.showCreateLocationModal = false;
    },
    handleNewLocation(location) {
      if(this.createLocationType == null) {
        this.showCreateLocationModal = false;
        return;
      }

      if(this.createLocationType === 1) {
        this.scheduledServiceTime.location_from_id = location.id;
        this.scheduledServiceTime.location_from = location;
      } else if(this.createLocationType === 2) {
        this.scheduledServiceTime.location_to = location;
        this.scheduledServiceTime.location_to_id = location.id;
      }

      this.closeLocationModal();
    }
  },
};
</script>

<template>
  <div>
    <BlockingContainer v-if="$wait.is('scheduled-service-time loading')"></BlockingContainer>
    <b-form data-vv-scope="scheduled-service-time-form" @submit.prevent="validate">
      <b-form-checkbox
        v-model="useExistingCar"
        :disabled="scheduledServiceTime.report_id != null || !isJobEditable(job)"
        name="check-button"
        switch
        class="mb-2"
        @change="resetValidation"
      >
        {{ $t('fields.use_existing_car') }}
      </b-form-checkbox>
      <b-form-group v-if="!useExistingCar">
        <template v-slot:label class="col-form-legend col-12">
          {{ $t('fields.car_type') }}:
          <span class="status-star text-danger">*</span>
          <span class="near-label-info text-secondary" @click.stop>
            <FontAwesomeIcon id="carTypePopover" :icon="['fas', 'info-circle']" tabindex="-1" />
            <b-popover target="carTypePopover" triggers="focus" placement="rightbottom">
              {{ $t('infos.car_type') }}
            </b-popover>
          </span>
        </template>
        <b-form-select
          v-model="scheduledServiceTime.car_type_id"
          v-validate="'required'"
          data-vv-value-path="localValue"
          data-vv-name="car_type_id"
          :disabled="scheduledServiceTime.report_id != null || !isJobEditable(job)"
          :data-vv-as="$t('fields.car_type')"
          :state="errors.has('car_type_id', 'scheduled-service-time-form') ? false : null"
          aria-describedby="input-feedback-car_type_id"
        >
          <option :value="null">{{ $t('placeholders.car_type') }}</option>
          <option v-for="carType in carTypes" :key="`carTypeSelect${carType.id}`" :value="carType.id">
            {{ carType.name }}
          </option>
        </b-form-select>
        <b-form-invalid-feedback id="input-feedback-car_type_id">
          <template v-show="errors.has('car_type_id', 'scheduled-service-time-form')">{{
            errors.first('car_type_id', 'scheduled-service-time-form')
          }}</template>
        </b-form-invalid-feedback>
      </b-form-group>
      <CarForm
        v-else
        v-model="scheduledServiceTime"
        :loaded-id="scheduledServiceTime.car_id"
        :is-car-use-form="true"
        :disabled="scheduledServiceTime.report_id != null || !isJobEditable(job)"
        >&nbsp;</CarForm
      >
      <b-form-row>
        <b-col sm="3">
          <b-form-group>
            <template v-slot:label class="col-form-legend col-12">
              {{ $t('fields.location_from') }}:
              <span class="status-star text-danger">*</span>
            </template>
            <b-input-group>
              <div class="form-control border-muted p-0">
                <EntityMultiselect
                  v-model="scheduledServiceTime.location_from_id"
                  v-validate="'required'"
                  data-vv-name="location_from_id"
                  :disabled="scheduledServiceTime.report_id != null || !isJobEditable(job)"
                  :data-vv-as="$t('fields.location_from')"
                  :initial-value="scheduledServiceTime.location_from"
                  :state="errors.has('location_from_id', 'scheduled-service-time-form')"
                  :placeholder="$t('placeholders.location')"
                  :model="locationModel"
                >
                </EntityMultiselect>
              </div>
              <b-input-group-append>
                <b-btn class="bg-light-gray border-gray" variant="secondary" size="sm" @click="showLocationModal(1)">
                  <FontAwesomeIcon :icon="['fas', 'plus']" />
                </b-btn>
              </b-input-group-append>
            </b-input-group>
          </b-form-group>
        </b-col>
        <b-col sm="3">
          <b-form-group>
            <template v-slot:label class="col-form-legend col-12">
              {{ $t('fields.location_to') }}:
              <span class="status-star text-danger">*</span>
            </template>
            <b-input-group>
              <div class="form-control border-muted p-0">
                <EntityMultiselect
                  v-model="scheduledServiceTime.location_to_id"
                  v-validate="'required'"
                  data-vv-name="location_to_id"
                  :disabled="scheduledServiceTime.report_id != null || !isJobEditable(job)"
                  :data-vv-as="$t('fields.location_to')"
                  :initial-value="scheduledServiceTime.location_to"
                  :state="errors.has('location_to_id', 'scheduled-service-time-form')"
                  :placeholder="$t('placeholders.location')"
                  :model="locationModel"
                >
                </EntityMultiselect>
              </div>
              <b-input-group-append>
                <b-btn class="bg-light-gray border-gray" variant="secondary" size="sm" @click="showLocationModal(2)">
                  <FontAwesomeIcon :icon="['fas', 'plus']" />
                </b-btn>
              </b-input-group-append>
            </b-input-group>
          </b-form-group>
        </b-col>
        <b-col sm="3">
          <b-form-group>
            <template v-slot:label class="col-form-legend col-12">
              {{ $t('fields.service_start') }}:
              <span class="status-star text-danger">*</span>
            </template>
            <flat-pickr
              ref="serviceStart"
              v-model="scheduledServiceTime.start_timestamp"
              v-validate="'required'"
              data-vv-name="start_timestamp"
              :data-vv-as="$t('fields.service_start')"
              :disabled="scheduledServiceTime.report_id != null || !isJobEditable(job)"
              aria-describedby="input-feedback-start_timestamp"
              :config="dateConfig"
              class="form-control"
              :class="{ 'is-invalid': errors.has('start_timestamp', 'scheduled-service-time-form') }"
              :placeholder="$t('placeholders.service_start')"
              @on-change="handleTimeChange(...arguments, 'start_timestamp', 'previousStartHour')"
              @on-close="
                closeCalendar('scheduledServiceTime.start_timestamp', 'dd.MM.yyyy HH:mm', 'yyyy-MM-dd HH:mm:ss', ...arguments);
                assignServiceTimeDateValueFromTo('start_timestamp', 'end_timestamp');
              "
            >
            </flat-pickr>
            <b-form-invalid-feedback id="input-feedback-start_timestamp">
              <template v-show="errors.has('start_timestamp', 'scheduled-service-time-form')"
                >{{ errors.first('start_timestamp', 'scheduled-service-time-form') }}
              </template>
            </b-form-invalid-feedback>
          </b-form-group>
        </b-col>
        <b-col sm="3">
          <b-form-group>
            <template v-slot:label class="col-form-legend col-12">
              {{ $t('fields.service_end') }}:
              <span class="status-star text-danger">*</span>
            </template>
            <flat-pickr
              ref="serviceEnd"
              v-model="scheduledServiceTime.end_timestamp"
              v-validate="'required|isBigger:serviceStart'"
              data-vv-name="end_timestamp"
              :data-vv-as="$t('fields.service_end')"
              :disabled="scheduledServiceTime.report_id != null || !isJobEditable(job)"
              aria-describedby="input-feedback-end_timestamp"
              :config="dateConfig"
              class="form-control"
              :class="{ 'is-invalid': errors.has('end_timestamp', 'scheduled-service-time-form') }"
              :placeholder="$t('placeholders.service_end')"
              @on-change="handleTimeChange(...arguments, 'end_timestamp', 'previousEndHour')"
              @on-close="closeCalendar('scheduledServiceTime.end_timestamp', 'dd.MM.yyyy HH:mm', 'yyyy-MM-dd HH:mm:ss', ...arguments)"
            >
            </flat-pickr>
            <b-form-invalid-feedback id="input-feedback-end_timestamp">
              <template v-show="errors.has('end_timestamp', 'scheduled-service-time-form')"
                >{{ errors.first('end_timestamp', 'scheduled-service-time-form') }}
              </template>
            </b-form-invalid-feedback>
          </b-form-group>
        </b-col>
      </b-form-row>
      <b-form-row>
        <b-col :sm="loadedId ? 6 : 3">
          <b-form-group :label="`${$t('fields.executor')}:`">
            <b-form-radio-group
              v-model="executorSelectedOption"
              buttons
              button-variant="outline-secondary border-gray"
              :options="executorOptions"
              :disabled="scheduledServiceTime.report_id != null || !isJobEditable(job)"
              class="executor-radio-group w-100"
              @change="onExecutorChange"
            >
            </b-form-radio-group>
          </b-form-group>
        </b-col>
        <b-col :sm="loadedId ? 6 : 9">
          <b-form-group v-if="executorSelectedOption == 0">
            <template v-slot:label> {{ $t('fields.driver') }}: </template>
            <EntityMultiselect
              key="driver-input"
              v-model="scheduledServiceTime.driver_id"
              v-validate="''"
              data-vv-name="driver_id"
              :data-vv-as="$t('fields.driver')"
              :disabled="scheduledServiceTime.report_id != null || !isJobEditable(job)"
              :initial-value="scheduledServiceTime.driver"
              :state="errors.has('driver_id', 'scheduled-service-time-form')"
              :placeholder="$t('placeholders.driver')"
              sort="first_name"
              :model="driverModel"
            >
            </EntityMultiselect>

            <div v-show="errors.has('driver_id', 'scheduled-service-time-form')" class="invalid-feedback d-block m-0">
              {{ errors.first('driver_id', 'scheduled-service-time-form') }}
            </div>
          </b-form-group>
          <b-form-group v-else>
            <template v-slot:label> {{ $t('fields.service_provider') }}: </template>
            <EntityMultiselect
              v-model="scheduledServiceTime.service_provider_id"
              v-validate="''"
              data-vv-name="service_provider_id"
              :data-vv-as="$t('fields.service_provider')"
              :disabled="scheduledServiceTime.report_id != null || !isJobEditable(job)"
              :initial-value="scheduledServiceTime.service_provider"
              :state="errors.has('service_provider_id', 'scheduled-service-time-form')"
              :placeholder="$t('placeholders.service_provider')"
              :model="serviceProviderModel"
            >
            </EntityMultiselect>
            <div v-show="errors.has('service_provider_id', 'scheduled-service-time-form')" class="invalid-feedback d-block m-0">
              {{ errors.first('service_provider_id', 'scheduled-service-time-form') }}
            </div>
          </b-form-group>
        </b-col>
      </b-form-row>
      <b-form-row>
        <b-col sm="12">
          <b-form-group label-class="col-form-legend">
            <template v-slot:label> {{ $t('fields.remark') }}: </template>
            <b-form-textarea
              id="remark"
              v-model="scheduledServiceTime.remark"
              v-validate="'max:65000'"
              data-vv-value-path="localValue"
              data-vv-name="remark"
              :disabled="!isJobEditable(job)"
              :rows="1"
              :state="errors.has('remark', 'scheduled-service-time-form') ? false : null"
              aria-describedby="input-feedback-remark"
              class="single-line-textarea"
            >
            </b-form-textarea>
          </b-form-group>
        </b-col>
      </b-form-row>
      <b-form-row>
        <b-col sm="12">
          <b-form-group :label="`${$t('fields.report')}:`">
            <b-input-group>
              <div class="form-control border-muted p-0">
                <EntityMultiselect
                  key="report-input"
                  v-model="scheduledServiceTime.report_id"
                  v-validate="''"
                  data-vv-name="report_id"
                  :disabled="!isJobEditable(job)"
                  :data-vv-as="$t('fields.report')"
                  :initial-value="scheduledServiceTime.report"
                  :state="errors.has('report_id', 'scheduled-service-time-form')"
                  :placeholder="$t('placeholders.report')"
                  sort="id"
                  :model="reportModel"
                  :select="reportMultiselectSelect"
                  :where="reportMultiselectWhere"
                  :include="reportMultiselectInclude"
                  :params="reportMultiselectURLParams"
                  reference="SST"
                >
                </EntityMultiselect>
              </div>
              <b-input-group-append>
                <b-btn variant="dark" :disabled="!scheduledServiceTime.report_id || !isJobEditable(job)" @click="clearReport()">{{ $t('actions.remove') }}</b-btn>
              </b-input-group-append>
            </b-input-group>
            <div v-show="errors.has('report_id', 'scheduled-service-time-form')" class="invalid-feedback d-block m-0">
              {{ errors.first('report_id', 'scheduled-service-time-form') }}
            </div>
          </b-form-group>
        </b-col>
      </b-form-row>
      <b-form-row class="mb-3">
        <b-col>
          <b-button
            ref="scheduledServiceTimeSubmitBtn"
            type="submit"
            :disabled="errors.any('scheduled-service-time-form') || !isJobEditable(job)"
            :variant="'outline-' + (!errors.any('scheduled-service-time-form') ? 'dark' : 'danger')"
          >
            {{ $t('actions.submit') }}
          </b-button>
          <b-button v-if="loadedId" ref="scheduledServiceTimeDeleteBtn" variant="outline-danger" class="ml-2" @click="deleteScheduledServiceTime">
            {{ $t('actions.delete') }}
          </b-button>
        </b-col>
      </b-form-row>
    </b-form>
    <b-modal
      ref="location-modal"
      v-model="showCreateLocationModal"
      size="lg"
      hide-footer
      :title="$t('titles.create_location')"
      static
      @hide="closeLocationModal"
    >
      <div class="row">
        <div class="col">
          <LocationForm :areas="areas" :is-modal="true" @submitted="handleNewLocation" />
        </div>
      </div>
    </b-modal>
  </div>
</template>

<style lang="css" scoped>
>>> .executor-radio-group label {
  width: 100%;
}

.btn-group-toggle .btn {
  flex: 1;
  min-width: 150px;
}

.form-check-inline .btn,
.btn-group .btn-outline-secondary {
  flex: 1;
  min-width: 150px;
}

.input-group-append .btn {
  min-width: 80px;
}

.input-group-append .btn-secondary,
.input-group-append .btn-outline-secondary {
  min-width: auto;
}

.input-group-append .btn-dark {
  min-width: 80px;
}

/* Make textarea start at input field height but allow expansion */
.single-line-textarea {
  min-height: calc(1.5em + 0.75rem + 2px);
  height: calc(1.5em + 0.75rem + 2px);
  resize: vertical;
  line-height: 1.5;
  overflow-y: auto;
}

/* Allow natural expansion when typing */
.single-line-textarea:focus {
  height: auto;
  min-height: calc(1.5em + 0.75rem + 2px);
}
</style>
